// features/admin/userMonitorApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// ─── Shared ───────────────────────────────────────────────────────────────────

export type UserType     = 'registered' | 'guest';
export type FilterMode   = 'all' | 'app' | 'web';
export type WebUserType  = 'all' | 'registered' | 'guest';  // For web mode filtering

// ─── Unified User Entry ───────────────────────────────────────────────────────

export interface UnifiedUserProfile {
  user_type:    UserType;
  user_id:      number;
  full_name:    string;
  email:        string;
  contact:      string;
  country_code: string | null;
  dob:          string | null;
  anniversary:  string | null;
  referral_code: string | null;
}

export interface UnifiedUserDevice {
  device_id:    string | null;
  device_name:  string | null;
  os_version:   string | null;
  model_number: string | null;
  push_enabled:  boolean;
  app_installed: boolean;
}

export interface UnifiedUserAccount {
  status:            number;
  status_label:      string;
  login_type:        string;
  registered_at:     string;
  last_active_at:    string | null;
  days_since_active: number | null;
}

export interface UnifiedUserMembership {
  plan_id:           number | null;
  plan_name:         string;
  member_label:      string;
  subscription_date: string | null;
  expiry:            string | null;
  recurring_label:   string | null;
  is_expired:        boolean | null;
}

export interface UnifiedUserWallet {
  balance: number;
}

export interface UnifiedUserReferrals {
  total_referrals:      number;
  successful_referrals: number;
  total_reward_earned:  number;
}

export interface UnifiedUserTodayStats {
  orders:          number;
  delivered:       number;
  cancelled:       number;
  spend:           number;
  product_views:   number;
  purchase_events: number;
}

export interface UnifiedUserPeriodStats {
  from_date:          string;
  to_date:            string;
  orders:             number;
  delivered:          number;
  cancelled:          number;
  spend:              number;
  coupon_savings:     number;
  cod_orders:         number;
  pay_later_orders:   number;
  wallet_orders:      number;
  first_order_date:   string | null;
  last_order_date:    string | null;
  product_views:      number;
  purchase_events:    number;
  unique_products:    number;
  categories_browsed: number;
}

export interface UnifiedUserPerformance {
  total_orders:          number;
  total_delivered:       number;
  total_failed_delivery: number;
  total_cancelled:       number;
  success_rate:          number;
  total_spend:           number;
  total_coupon_savings:  number;
  total_wallet_used:     number;
  total_reward_discount: number;
  cod_orders:            number;
  pay_later_orders:      number;
  first_order_date:      string | null;
  last_order_date:       string | null;
  total_views:           number;
  total_purchase_events: number;
  unique_products:       number;
  categories_browsed:    number;
  first_activity:        string | null;
  last_activity:         string | null;
  rating:                string | number | null;
}

export interface RecentOrder {
  order_id:     number;
  invoice_no:   string;
  status:       string;
  status_code:  number;
  amount:       number;
  cod?:         boolean;
  pay_later?:   boolean;
  wallet_used?: boolean;
  order_date:   string;
  delivered_at?: string | null;
}

export interface UnifiedUserEntry {
  profile:       UnifiedUserProfile;
  device:        UnifiedUserDevice;
  account:       UnifiedUserAccount;
  membership:    UnifiedUserMembership | null;
  wallet:        UnifiedUserWallet     | null;
  referrals:     UnifiedUserReferrals  | null;
  today:         UnifiedUserTodayStats;
  period:        UnifiedUserPeriodStats;
  performance:   UnifiedUserPerformance;
  recent_orders: RecentOrder[];
}

// ─── Unified Summary ──────────────────────────────────────────────────────────

export interface UnifiedSummary {
  filter_mode: FilterMode;
  registered: {
    total:          number;
    active:         number;
    inactive:       number;
    app_installed:  number;
    push_enabled:   number;
    social_logins:  number;
    new_today:      number;
    new_this_week:  number;
    new_this_month: number;
  };
  memberships: {
    subscribed: number;
    silver:     number;
    gold:       number;
    platinum:   number;
  };
  guests: {
    total:     number;
    new_today: number;
  };
  today: {
    reg_orders:       number;
    reg_revenue:      number;
    guest_orders:     number;
    guest_revenue:    number;
    total_orders:     number;
    total_revenue:    number;
    product_views:    number;
    purchase_events:  number;
    unique_visitors:  number;
    active_app_users: number;
  };
}

// ─── Request / Response ───────────────────────────────────────────────────────

export interface UnifiedUserMonitorRequest {
  filter_mode?:    FilterMode;
  user_type?:      WebUserType;  // For web mode filtering
  user_id?:        number;
  guest_user_id?:  number;
  status?:         0 | 1;
  plan_id?:        number;
  search?:         string;
  from_date?:      string;
  to_date?:        string;
  page?:           number;
  limit?:          number;
  download?:       0 | 1 | 2;
}

export interface UnifiedUserMonitorResponse {
  error:       boolean;
  message:     string;
  filter_mode: FilterMode;
  summary:     UnifiedSummary;
  pagination: {
    total: number;
    page:  number;
    limit: number;
    pages: number;
  };
  data: UnifiedUserEntry[];
}

// ─── Endpoints ────────────────────────────────────────────────────────────────

export const USER_MONITOR_ENDPOINTS = {
  UNIFIED_USER_MONITOR: '/admin/unified-user-monitor-list',
} as const;

// ─── API Slice ────────────────────────────────────────────────────────────────

export const userMonitorApi = createApi({
  reducerPath: 'userMonitorApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_ENDPOINT,
    prepareHeaders: (headers) => {
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      const token = localStorage.getItem('admin_token');
      if (token) headers.set('Authorization', `Bearer ${token}`);
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['UnifiedUsers'],
  endpoints: (build) => ({

    unifiedUserMonitorList: build.query<UnifiedUserMonitorResponse, UnifiedUserMonitorRequest | void>({
      query: (params) => ({
        url:    USER_MONITOR_ENDPOINTS.UNIFIED_USER_MONITOR,
        method: 'POST',
        body:   params ? { ...params } : { filter_mode: 'all' },
      }),
      serializeQueryArgs: ({ queryArgs, endpointName }) => {
        const p = queryArgs as UnifiedUserMonitorRequest | undefined;
        return `${endpointName}-${p?.filter_mode ?? 'all'}-${p?.user_type ?? 'all'}-${p?.page ?? 1}-${p?.plan_id ?? ''}-${p?.search ?? ''}`;
      },
      providesTags: ['UnifiedUsers'],
      transformResponse: (response: any): UnifiedUserMonitorResponse => ({
        error:       response?.error       ?? false,
        message:     response?.message     || '',
        filter_mode: response?.filter_mode || 'all',
        summary:     response?.summary     || {
          filter_mode:  'all',
          registered:   { total: 0, active: 0, inactive: 0, app_installed: 0, push_enabled: 0, social_logins: 0, new_today: 0, new_this_week: 0, new_this_month: 0 },
          memberships:  { subscribed: 0, silver: 0, gold: 0, platinum: 0 },
          guests:       { total: 0, new_today: 0 },
          today:        { reg_orders: 0, reg_revenue: 0, guest_orders: 0, guest_revenue: 0, total_orders: 0, total_revenue: 0, product_views: 0, purchase_events: 0, unique_visitors: 0, active_app_users: 0 },
        },
        pagination: response?.pagination || { total: 0, page: 1, limit: 20, pages: 0 },
        data:       Array.isArray(response?.data) ? response.data : [],
      }),
      transformErrorResponse: (response: any) => ({
        error:       true,
        message:     response.data?.message || 'Failed to load user monitor data',
        filter_mode: 'all' as FilterMode,
        summary:     { filter_mode: 'all', registered: {}, memberships: {}, guests: {}, today: {} },
        pagination:  { total: 0, page: 1, limit: 20, pages: 0 },
        data:        [],
      }),
    }),
  }),
});

export const {
  useUnifiedUserMonitorListQuery,
} = userMonitorApi;